import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../hooks/useAuthStore';
import LegalModal from '../Legal/LegalModal';
import api from '../../services/api';
import { enterDemoMode, DEMO_USER } from '../../services/demo';
import '../../styles/auth.css';

export default function AuthPage({ mode }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [inviterName, setInviterName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuthStore();

  const handleDemoMode = () => {
    enterDemoMode();
    useAuthStore.setState({
      user: { ...DEMO_USER, status: DEMO_USER.statusText },
      token: null,
      isAuthenticated: true,
    });
    navigate('/');
  };

  useEffect(() => {
    const invite = searchParams.get('invite');
    const inviteEmail = searchParams.get('email');
    if (mode === 'register' && invite) {
      setInviteToken(invite);
      if (inviteEmail) setEmail(inviteEmail);
      api.get(`/invites/${invite}`).then((res) => {
        if (res.data?.inviterName) setInviterName(res.data.inviterName);
      }).catch(() => {});
    }
    if (searchParams.get('confirmed') === '1') {
      setSuccess('Email подтверждён! Теперь вы можете войти.');
    }
  }, []);

  function isStrongPassword(pwd) {
    if (pwd.length < 8) return 'Пароль должен содержать минимум 8 символов';
    if (!/[A-Z]/.test(pwd)) return 'Пароль должен содержать заглавную букву';
    if (!/[a-z]/.test(pwd)) return 'Пароль должен содержать строчную букву';
    if (!/\d/.test(pwd)) return 'Пароль должен содержать цифру';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) return 'Пароль должен содержать спецсимвол';
    return null;
  }

  const handleForgotPassword = async () => {
    if (!email) { setError('Введите email'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setSuccess(res.data.message || 'Ссылка для сброса пароля отправлена на вашу почту');
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    if (mode === 'register') {
      const pwdError = isStrongPassword(password);
      if (pwdError) { setError(pwdError); return; }
      if (password !== confirmPassword) { setError('Пароли не совпадают'); return; }
      if (!agree) { setError('Необходимо согласиться с политикой конфиденциальности и правилами пользования'); return; }
    }

    setLoading(true);
    try {
      if (mode === 'register') {
        await register(email, password, displayName, inviteToken);
        navigate('/confirm-email');
      } else {
        const u = await login(email, password);
        navigate(u?.isAdmin ? '/admin' : '/');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Ошибка';
      setError(msg);
      if (msg && msg.toLowerCase().includes('email') && msg.toLowerCase().includes('подтвержд')) {
        navigate(`/confirm-email?email=${encodeURIComponent(email)}`);
        return;
      }
    }
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/tryndi.png" className="auth-wordmark" alt="Трынди" />
          <p>просто общайся</p>
        </div>
        <form onSubmit={handleSubmit}>
          <h2>{mode === 'register' ? 'Регистрация' : 'Вход'}</h2>
          {inviterName && (
            <div className="auth-success">📨 Вас пригласил(а) {inviterName}</div>
          )}
          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}
          {mode === 'register' && (
            <input type="text" placeholder="Имя" value={displayName}
              onChange={(e) => setDisplayName(e.target.value)} required />
          )}
          <input type="email" placeholder="Email" value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }} required />

          <div className="password-wrapper">
            <input type={showPassword ? 'text' : 'password'} placeholder="Пароль" value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); setSuccess(''); }}
              required minLength={8} />
            <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? '🙈' : '👁️'}
            </span>
          </div>

          {mode === 'register' && (
            <div className="password-wrapper">
              <input type={showConfirmPassword ? 'text' : 'password'} placeholder="Подтвердите пароль"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); setSuccess(''); }}
                required minLength={8} />
              <span className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? '🙈' : '👁️'}
              </span>
            </div>
          )}

          {mode === 'register' && password && (
            <div className={`password-strength ${isStrongPassword(password) ? 'weak' : 'strong'}`}>
              {isStrongPassword(password) || '✓ Надёжный пароль'}
            </div>
          )}

          {mode === 'register' && (
            <label className="auth-agree">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>
                Я принимаю{' '}
                <a href="#!" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }}>политику конфиденциальности</a>{' '}
                и{' '}
                <a href="#!" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }}>правила пользования</a>
              </span>
            </label>
          )}

          <button type="submit" disabled={loading}>
            {loading ? 'Загрузка...' : (mode === 'register' ? 'Зарегистрироваться' : 'Войти')}
          </button>

          {mode !== 'register' && (
            <button type="button" className="auth-link-btn" onClick={handleForgotPassword} disabled={loading}>
              Забыли пароль?
            </button>
          )}

          <button type="button" className="auth-link-btn auth-demo-btn" onClick={handleDemoMode} disabled={loading}>
            👀 Демо-режим — посмотреть без регистрации
          </button>
        </form>
        <p className="auth-switch">
          {mode === 'register' ? (
            <>Уже есть аккаунт? <a href="/login">Войти</a></>
          ) : (
            <>Нет аккаунта? <a href="/register">Регистрация</a></>
          )}
        </p>
        <div className="auth-store">
          <a
            className="auth-rustore-btn"
            href="https://www.rustore.ru/catalog/app/com.trandi"
            target="_blank"
            rel="noopener noreferrer"
          >
            Скачать в RuStore
          </a>
          <p className="auth-apk">
            <a href="/TrandI.apk?v=1.126" download>Скачать APK для Android (v1.126)</a>
          </p>
        </div>
      </div>
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
    </div>
  );
}
